import { isMediaImageReady, setImageTimer } from '@/helpers/mediaUtils';
import { isReelLike } from '@/helpers/isReelLike';
import { animateCurrentProgressBar, prepareCurrentProgressBar } from '@/modules/actions/headerLineDuration';
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { handleArrow } from '@/modules/actions/desktop/desktopArrowIconsAction';
import { closeModal } from '@/helpers/closeModal';
import { isCampaignAutoPlayEnabled, isCampaignLoopPlaybackEnabled } from '@/helpers/campaignSettings';
import { queryHeaderElements } from '@/helpers/headerShadow';

let cleanupCurrentMediaAutoPlay = null;
let videoFallbackTimerId = null;
let videoFallbackStartedAt = null;
let videoFallbackDurationMs = null;
let videoFallbackRemainingMs = null;
let videoFallbackCallback = null;

const clearVideoFallbackTimer = () => {
  if (videoFallbackTimerId !== null) {
    clearTimeout(videoFallbackTimerId);
    videoFallbackTimerId = null;
  }
  videoFallbackStartedAt = null;
  videoFallbackDurationMs = null;
};

const resetVideoFallbackState = () => {
  clearVideoFallbackTimer();
  videoFallbackRemainingMs = null;
  videoFallbackCallback = null;
};

const scheduleVideoFallbackTimer = (callback, durationMs) => {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return;
  clearVideoFallbackTimer();
  videoFallbackCallback = callback;
  videoFallbackStartedAt = Date.now();
  videoFallbackDurationMs = durationMs;
  videoFallbackRemainingMs = null;
  videoFallbackTimerId = window.setTimeout(callback, durationMs);
};

const pauseVideoFallbackTimer = () => {
  if (videoFallbackTimerId === null) return;
  if (!Number.isFinite(videoFallbackDurationMs) || videoFallbackStartedAt === null) return;
  const elapsed = Date.now() - videoFallbackStartedAt;
  videoFallbackRemainingMs = Math.max(0, videoFallbackDurationMs - elapsed);
  clearVideoFallbackTimer();
};

const resumeVideoFallbackTimer = () => {
  if (videoFallbackTimerId !== null) return;
  if (!Number.isFinite(videoFallbackRemainingMs) || videoFallbackRemainingMs <= 0) return;
  if (typeof videoFallbackCallback !== 'function') return;
  scheduleVideoFallbackTimer(videoFallbackCallback, videoFallbackRemainingMs);
};

export const pauseDesktopAutoPlayNextAction = () => {
  if (typeof cleanupCurrentMediaAutoPlay === 'function') {
    cleanupCurrentMediaAutoPlay();
    cleanupCurrentMediaAutoPlay = null;
  }
  resetVideoFallbackState();
};

// 動画終了後に次の動画を自動再生する関数
export const setupAutoPlayNextAction = () => {
  pauseDesktopAutoPlayNextAction();

  const storeeelItems = window.storeeelItems;
  const autoPlayEnabled = isCampaignAutoPlayEnabled(storeeelItems);
  const loopPlaybackEnabled = isCampaignLoopPlaybackEnabled(storeeelItems);

  const isAtCampaignEnd = () => {
    if (loopPlaybackEnabled) return false;

    if (storeeelItems?.frame === 'circle') {
      const groups = storeeelItems?.contentGroups || [];
      if (groups.length === 0) return false;

      const currentGroupId = String(window.iconId || '').split('-').pop();
      const currentGroupIndex = groups.findIndex(g => String(g?.id) === String(currentGroupId));
      if (currentGroupIndex < 0) return false;

      const currentGroup = groups[currentGroupIndex];
      const lastGroupIndex = groups.length - 1;
      const lastContentIndex = Math.max(0, (currentGroup?.contents?.length || 1) - 1);
      const currentContentIndex = Number(window.moviePosition || 0);

      return currentGroupIndex === lastGroupIndex && currentContentIndex >= lastContentIndex;
    }

    if (isReelLike(storeeelItems?.frame)) {
      const allContents = (storeeelItems?.contentGroups || []).flatMap(g => g.contents || []);
      if (allContents.length === 0) return false;
      const lastIndex = allContents.length - 1;
      const currentIndex = Number(window.moviePosition || 0);
      return currentIndex >= lastIndex;
    }

    return false;
  };

  const movie = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .storeeel-movie');
  if (movie) {
    let hasTriggered = false;

    const triggerNext = () => {
      if (hasTriggered) return;
      hasTriggered = true;
      cleanup();

      // 自動再生OFFでも、最終コンテンツまで到達したら閉じる
      if (isAtCampaignEnd()) {
        closeModal();
        return;
      }

      if (!autoPlayEnabled) {
        return;
      }

      handleArrow('next');
    };

    const scheduleFallback = () => {
      if (!Number.isFinite(movie.duration) || movie.duration <= 0) return;
      const bufferMs = 500;
      const fallbackMs = Math.max(1000, Math.ceil(movie.duration * 1000) + bufferMs);
      scheduleVideoFallbackTimer(triggerNext, fallbackMs);
    };

    const handleLoadedMetadata = () => {
      scheduleFallback();
    };

    const handleTimeUpdate = () => {
      if (!Number.isFinite(movie.duration) || movie.duration <= 0) return;
      const remaining = movie.duration - movie.currentTime;
      if (remaining <= 0.3) {
        triggerNext();
      }
    };

    const handleBuffering = () => {
      pauseVideoFallbackTimer();
    };

    const handlePlaying = () => {
      resumeVideoFallbackTimer();
    };

    const cleanup = () => {
      movie.removeEventListener('ended', triggerNext);
      movie.removeEventListener('loadedmetadata', handleLoadedMetadata);
      movie.removeEventListener('timeupdate', handleTimeUpdate);
      movie.removeEventListener('waiting', handleBuffering);
      movie.removeEventListener('stalled', handleBuffering);
      movie.removeEventListener('pause', handleBuffering);
      movie.removeEventListener('playing', handlePlaying);
      resetVideoFallbackState();
      cleanupCurrentMediaAutoPlay = null;
    };

    movie.addEventListener('ended', triggerNext);
    movie.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    movie.addEventListener('timeupdate', handleTimeUpdate);
    movie.addEventListener('waiting', handleBuffering);
    movie.addEventListener('stalled', handleBuffering);
    movie.addEventListener('pause', handleBuffering);
    movie.addEventListener('playing', handlePlaying);

    if (movie.readyState >= 1) scheduleFallback();

    cleanupCurrentMediaAutoPlay = cleanup;
  }

  // 画像は5秒後に次のメディアを表示
  const image = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .storeeel-media-image');
  if (image) {
    if (!autoPlayEnabled) {
      return;
    }
    const wrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');

    const progressIndex = window.storeeelItems.frame === 'circle'
      ? (window.moviePosition || 0)
      : 0;

    const banners = queryHeaderElements('.storeeel-movie-select_banner');
    const targetBanner = banners[progressIndex];
    const isRunning = targetBanner && targetBanner.classList.contains('active');

    const startTimer = () => {
      setImageTimer(() => {
        handleArrow('next');
      }, 5000);
    };

    if (isRunning) {
      hideMediaLoadingShimmer(wrapper);
      startTimer();
      return;
    }
    showMediaLoadingShimmer(wrapper);

    if (image._onLoadForAutoPlay) {
      image.removeEventListener('load', image._onLoadForAutoPlay);
      image._onLoadForAutoPlay = null;
    }
    if (image._onErrorForAutoPlay) {
      image.removeEventListener('error', image._onErrorForAutoPlay);
      image._onErrorForAutoPlay = null;
    }

    const cleanup = () => {
      if (image._onLoadForAutoPlay) {
        image.removeEventListener('load', image._onLoadForAutoPlay);
        image._onLoadForAutoPlay = null;
      }
      if (image._onErrorForAutoPlay) {
        image.removeEventListener('error', image._onErrorForAutoPlay);
        image._onErrorForAutoPlay = null;
      }
      cleanupCurrentMediaAutoPlay = null;
    };

    const startProgress = () => {
      prepareCurrentProgressBar(progressIndex);
      animateCurrentProgressBar(5, progressIndex);
      hideMediaLoadingShimmer(wrapper);
      startTimer();
      cleanup();
    };

    const tryStartProgress = () => {
      if (!isMediaImageReady(image)) return false;
      startProgress();
      return true;
    };

    // 読み込み完了後に進捗を開始する
    image._onLoadForAutoPlay = () => { tryStartProgress(); };
    image._onErrorForAutoPlay = () => {
      hideMediaLoadingShimmer(wrapper);
      cleanup();
    };
    image.addEventListener('load', image._onLoadForAutoPlay);
    image.addEventListener('error', image._onErrorForAutoPlay);
    cleanupCurrentMediaAutoPlay = cleanup;
    tryStartProgress();
  }
}
